if (!customElements.get('pdp-recommendations-browser')) {
  customElements.define('pdp-recommendations-browser', class PdpRecommendationsBrowser extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-recommendations-track]');
      this.status = this.querySelector('[data-recommendations-status]');
      this.previous = this.querySelector('[data-recommendations-previous]');
      this.next = this.querySelector('[data-recommendations-next]');
      this.chips = Array.from(this.querySelectorAll('[data-recommendation-chip]'));
      this.limit = Math.max(Number(this.dataset.limit || 8), 1);
      this.cache = new Map();
      this.abortController = null;

      if (!this.track) return;

      this.cache.set('recommended', this.track.innerHTML);
      this.bindControls();
      this.updateArrows();
    }

    bindControls() {
      const amount = () => Math.max(this.track.clientWidth * 0.82, 280);

      this.previous?.addEventListener('click', () => {
        this.track.scrollBy({ left: -amount(), behavior: 'smooth' });
      });

      this.next?.addEventListener('click', () => {
        this.track.scrollBy({ left: amount(), behavior: 'smooth' });
      });

      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.track.scrollBy({ left: -amount(), behavior: 'smooth' });
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.track.scrollBy({ left: amount(), behavior: 'smooth' });
        }
      });

      this.addEventListener('click', (event) => {
        const reveal = event.target.closest('[data-recommendation-reveal]');
        if (reveal) {
          event.preventDefault();
          const card = reveal.closest('[data-recommendation-card]');
          const active = card?.classList.toggle('is-revealed') || false;
          reveal.setAttribute('aria-pressed', String(active));
          return;
        }

        const chip = event.target.closest('[data-recommendation-chip]');
        if (!chip || chip.disabled) return;
        this.activateChip(chip);
      });
    }

    async activateChip(chip) {
      const source = chip.dataset.source;
      const handle = chip.dataset.collectionHandle;
      const cacheKey = source === 'recommended' ? 'recommended' : `collection:${handle}`;

      this.chips.forEach((item) => {
        const active = item === chip;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });

      if (this.cache.has(cacheKey)) {
        this.render(this.cache.get(cacheKey));
        return;
      }

      if (source !== 'collection' || !handle) return;

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setLoading(true, 'Loading products…');

      try {
        const root = window.Shopify?.routes?.root || '/';
        const endpoint = `${root}collections/${encodeURIComponent(handle)}?view=pdp-recommendations`;
        const response = await fetch(endpoint, {
          headers: { Accept: 'text/html' },
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error(`Request failed with ${response.status}`);

        const text = await response.text();
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const results = documentFragment.querySelector('[data-pdp-recommendation-results]');
        const items = results ? Array.from(results.querySelectorAll('.pdp-recommendations__item')).slice(0, this.limit) : [];
        const html = items.map((item) => item.outerHTML).join('');

        if (!html) {
          this.track.innerHTML = '<p class="pdp-recommendations__empty">No products are available in this category.</p>';
          this.status.textContent = 'No products found.';
        } else {
          this.cache.set(cacheKey, html);
          this.render(html);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Recommendation category load failed:', error);
        this.status.textContent = 'Products could not be loaded. Please try again.';
      } finally {
        this.setLoading(false);
      }
    }

    render(html) {
      this.track.innerHTML = html;
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
      this.status.textContent = '';
      requestAnimationFrame(() => this.updateArrows());
    }

    setLoading(loading, message = '') {
      this.classList.toggle('is-loading', loading);
      this.chips.forEach((chip) => { chip.disabled = loading; });
      this.status.textContent = message;
      this.track.setAttribute('aria-busy', String(loading));
    }

    updateArrows() {
      if (!this.previous || !this.next) return;
      const max = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
    }
  });
}
